import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useOrders } from '@/context/OrdersContext';
import { BrokerSelector } from '@/components/BrokerSelector';
import { AllBrokerRepo } from '@trader-dashboard/shared';

interface ImportOrdersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  onImportComplete: () => void;
}

interface ImportOrdersResponse {
  success: boolean;
  importedCount: number;
  errors: string[];
}

export const ImportOrdersDialog = ({
  open,
  onOpenChange,
  accountId,
  onImportComplete,
}: ImportOrdersDialogProps): React.ReactNode => {
  const [pastedData, setPastedData] = useState('');
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [result, setResult] = useState<ImportOrdersResponse | undefined>(undefined);
  const [selectedBrokerId, setSelectedBrokerId] = useState(
    AllBrokerRepo.getDefaultBroker().id
  );

  const { importOrders } = useOrders();

  const handleImport = async () => {
    if (!pastedData.trim()) {
      setError('Please paste order data');
      return;
    }

    setImporting(true);
    setError(undefined);
    setResult(undefined);

    try {
      const response = await importOrders(pastedData, accountId, selectedBrokerId);

      setResult(response);

      // Only auto-close if successful and no errors
      if (response.success && response.errors.length === 0) {
        setTimeout(() => {
          onImportComplete();
          onOpenChange(false);
          setPastedData('');
          setResult(undefined);
        }, 2000);
      } else if (response.success) {
        // Success with some errors - call onImportComplete but keep dialog open
        onImportComplete();
      }
    } catch (err: any) {
      console.error('Error importing orders:', err);
      setError(err.message || 'Failed to import orders');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (!importing) {
      onOpenChange(false);
      setPastedData('');
      setError(undefined);
      setResult(undefined);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import Orders</DialogTitle>
          <DialogDescription>
            Paste your order data from the trading platform. Include the header row or just the data rows.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              Broker
            </label>
            <BrokerSelector
              value={selectedBrokerId}
              onValueChange={setSelectedBrokerId}
              disabled={importing}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Order Data
            </label>
            <Textarea
              value={pastedData}
              onChange={(e) => setPastedData(e.target.value)}
              placeholder="Ticket Open Time Type Size Item Price S / L T / P Close Time Price Commission Taxes Swap Profit&#10;28493265 2024.08.02 19:20:00 sell 0.20 usdjpy 147.085 0.000 147.000 2024.08.02 19:31:41 146.993 0.00 0.00 0.00 12.52"
              className="min-h-[200px] font-mono text-xs"
              disabled={importing}
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {result && (
            <Alert variant={result.success ? "default" : "destructive"}>
              <AlertDescription>
                {result.success ? (
                  <div>
                    <p className="font-semibold">Import Complete</p>
                    <p>{result.importedCount} orders imported successfully</p>
                    {result.errors.length > 0 && (
                      <div className="mt-2">
                        <p className="font-semibold text-yellow-600">
                          {result.errors.length} errors:
                        </p>
                        <ul className="list-disc list-inside text-xs">
                          {result.errors.slice(0, 5).map((err, idx) => (
                            <li key={idx}>{err}</li>
                          ))}
                          {result.errors.length > 5 && (
                            <li>... and {result.errors.length - 5} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  'Import failed'
                )}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={importing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={importing || !pastedData.trim()}
          >
            {importing ? 'Importing...' : 'Import Orders'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
